import { CloseOutlined } from "@ant-design/icons"
import { Drawer, notification } from "antd"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import OSAlert from "~/components/os-alert"
import { PeopleLoader } from "~/containers/person-brief/loader"
import PersonBrief from "~/containers/person-brief/person-details"
import { toPlainStringForApi } from "~/utils/location-helpers"

import EmailTemplate from "../account/email-template"
import DownloadPersonBrief from "./download-person-brief"
import { usePersonBrief } from "./use-person-brief"

export default function PersonBriefDrawer({
  account_name,
  linkedin_url,
  personName,
  accountId,
  accountDbId,
  renderMessageTemplate,
  isOpen,
  setIsOpen
}: {
  account_name: string | Record<string, unknown>
  linkedin_url: string
  personName: string | Record<string, unknown>
  accountId?: number
  // Internal DB account id — forwarded so the backend resolves the apollo
  // organization id itself (getSinglePersonBrief's accountDbId path). Pass
  // this when you have OUR account id rather than an apollo org id.
  accountDbId?: number | string | null
  renderMessageTemplate?: (props: {
    close: () => void
  }) => JSX.Element | null | any
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) {
  const { t } = useTranslation()
  const [notificationsApi, notificationContextHolder] =
    notification.useNotification()
  const { hash } = useLocation()

  const displayPersonName = toPlainStringForApi(personName)
  const displayAccountName = toPlainStringForApi(account_name)

  const urlWithoutProtcol = linkedin_url?.startsWith("https://www.")
    ? linkedin_url?.substring(12)
    : linkedin_url

  // Generation lives in usePersonBrief so Person 360 can run the same flow
  // inline. Behaviour here is unchanged: the drawer still starts on open and
  // resets on close.
  const source =
    hash === "#account-priorities"
      ? t("person_brief.label_account_priorities")
      : hash === "#org-chart"
        ? t("person_brief.label_org_chart")
        : t("person_brief.label_key_executive_changes")

  const {
    personBrief,
    persona,
    isApiFailed,
    isStreaming,
    isPersonBriefLoading,
    personaLoading,
    start,
    reset
  } = usePersonBrief({
    linkedin_url,
    personName: displayPersonName,
    account_name: displayAccountName,
    accountId,
    // Pre-existing bug, fixed in passing: this prop was accepted and
    // documented ("forwarded so the backend resolves the apollo organization
    // id itself") but never actually passed to the stream. People OS is the
    // only caller that supplies it and supplies nothing else, so every People
    // OS brief has been generating with no account context at all — neither
    // organization_id nor account_id on the wire.
    accountDbId,
    source,
    onMissingLinkedin: () =>
      notificationsApi.error({
        message: t("label_error"),
        description: t("person_brief.error_no_linkedin_url")
      })
  })

  const onClose = () => {
    reset()
    setIsOpen(false)
  }

  const onAfterOpenChange = async (open: boolean) => {
    if (!open) return onClose()
    await start()
  }

  return (
    <>
      {notificationContextHolder}
      {isOpen && (
        <Drawer
          afterOpenChange={onAfterOpenChange}
          width="70%"
          open={isOpen}
          footer={null}
          closeIcon={null}
          onClose={onClose}
          className="dark:!bg-dark-1 dark:text-dark-6 text-dark-2"
          title={
            <div className="flex items-center justify-between">
              <h3>
                {t("label_pre_meeting_brief")} {t("label_for")}{" "}
                <span className="text-primary dark:text-primary-light">
                  {displayPersonName}
                </span>
              </h3>
              <DownloadPersonBrief
                personName={displayPersonName}
                linkedin_url={urlWithoutProtcol}
                isDisabled={
                  personaLoading || isPersonBriefLoading || isStreaming
                }
              />
            </div>
          }
          extra={
            <button onClick={() => setIsOpen(false)}>
              <CloseOutlined className="text-lg dark:text-dark-6 text-dark" />
            </button>
          }
          maskClosable={false}
          prefixCls="person-brief-drawer"
          classNames={{
            header: "dark:!border-border",
            body: "!p-0"
          }}
        >
          <div className="flex flex-col gap-2">
            {isApiFailed ? (
              <div className="p-6 w-fit">
                <OSAlert
                  type="error"
                  message={t(
                    "person_brief.error_generating_pre_meeting_brief_support"
                  )}
                />
              </div>
            ) : personBrief ? (
              // Render the streamed brief as soon as the first frame lands; the
              // persona-dependent section (personality traits) fills in when
              // persona identification resolves. Do NOT gate the whole brief on
              // persona — that hid the progressive stream behind the loader.
              <PersonBrief
                people={personBrief}
                persona={persona}
                renderMessageTemplate={renderMessageTemplate}
                setIsOpen={setIsOpen}
                account_name={displayAccountName}
                accountId={accountId}
                streaming={isStreaming}
                personaLoading={personaLoading}
              />
            ) : isPersonBriefLoading || personaLoading ? (
              <div className="flex justify-center items-center h-full p-6">
                <PeopleLoader
                  people_names={[displayPersonName]}
                  account_name={displayAccountName}
                  dataReady={isPersonBriefLoading || personaLoading}
                />
              </div>
            ) : null}
          </div>
        </Drawer>
      )}
      <EmailTemplate />
    </>
  )
}
